/**
 * Subscription Management Layout
 * Provides header, navigation, and consistent layout for subscription management pages
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import {
    CreditCard,
    FileText,
    Clock,
    Settings,
    LogOut,
    Building2,
    ArrowLeft,
    ArrowRight,
    Target,
    BarChart3,
    Users,
    Award,
    ChevronDown,
    LayoutDashboard,
    Briefcase,
    Receipt,
    Menu,
    X,
} from 'lucide-react';

interface SubscriptionLayoutProps {
    children: React.ReactNode;
}

const SubscriptionLayout: React.FC<SubscriptionLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    // Only use organizationId if it's a valid organization (not default-org, default-org-id, or pending)
    // Note: user.id IS valid for individual subscriptions, but we don't use it as a fallback here
    // to prevent new users from being assigned an org before they choose subscription type
    const organizationId = user?.organizationId &&
        user.organizationId !== 'default-org' &&
        user.organizationId !== 'default-org-id' &&
        user.organizationId !== 'pending'
        ? user.organizationId
        : null;

    // For individual subscriptions, check subscription using user.id
    const subscriptionOrgId = organizationId || (user?.id || null);
    const { hasAccess, isLoading: subscriptionLoading, subscription } = useSubscriptionAccess(subscriptionOrgId);
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Check if this is an individual subscription (only when subscription is loaded)
    const isIndividualSubscription = !subscriptionLoading && subscription?.planType === 'individual';

    // Skip access check on onboarding and success pages - users need to access these pages to complete subscription setup
    const isOnboardingPage = location.pathname === '/onboarding';
    const isSuccessPage = location.pathname === '/subscription/success';
    const shouldSkipAccessCheck = isOnboardingPage || isSuccessPage;

    // Check if user owns/has admin access to the organization
    const isOrgOwner = user && (user.role === 'super_admin' || user.role === 'org_admin');

    const mainNavItems = [
        { name: 'Overview', href: '/subscription', icon: LayoutDashboard, requiresAccess: true },
    ];

    const managementMenu = {
        name: 'Management',
        icon: Briefcase,
        items: [
            { name: 'Challenges', href: '/subscription/challenges', icon: Target, requiresAccess: true },
            // Hide Organizations menu for individual subscriptions
            ...(isIndividualSubscription ? [] : [{ name: 'Organizations', href: '/subscription/organizations', icon: Building2, requiresAccess: true }]),
            { name: 'Users', href: '/subscription/users', icon: Users, requiresAccess: true },
            { name: 'Leaderboards', href: '/subscription/leaderboards', icon: Award, requiresAccess: true },
        ],
    };

    const analyticsMenu = {
        name: 'Analytics',
        icon: BarChart3,
        items: [
            { name: 'Reporting', href: '/subscription/reporting', icon: BarChart3, requiresAccess: true },
        ],
    };

    const billingMenu = {
        name: 'Billing',
        icon: Receipt,
        items: [
            { name: 'Invoices', href: '/subscription/invoices', icon: FileText, requiresAccess: true },
            { name: 'History', href: '/subscription/history', icon: Clock, requiresAccess: true },
        ],
    };

    // Only show Management and Analytics for org owners/admins
    const menuGroups = [
        ...(isOrgOwner ? [managementMenu, analyticsMenu] : []),
        billingMenu
    ];

    const handleLogout = async () => {
        await logout();
        navigate('/admin/login');
    };

    const isActive = (path: string) => {
        if (path === '/subscription') {
            return location.pathname === '/subscription' || location.pathname.startsWith('/subscription/');
        }
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
            {/* Header */}
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 border-b border-slate-200/50 dark:border-slate-800/50 shadow-sm shadow-slate-900/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        {/* Logo and Title */}
                        <div className="flex items-center space-x-4">
                            {/* Hamburger Menu Button - Mobile Only */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                aria-label="Toggle menu"
                            >
                                {mobileMenuOpen ? (
                                    <X className="w-6 h-6 text-slate-700 dark:text-slate-300" />
                                ) : (
                                    <Menu className="w-6 h-6 text-slate-700 dark:text-slate-300" />
                                )}
                            </button>
                            <button
                                onClick={() => navigate('/subscription')}
                                className="flex items-center space-x-4 hover:opacity-90 transition-all duration-200 group"
                            >
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-xl blur opacity-75 group-hover:opacity-100 transition-opacity"></div>
                                    <div className="relative p-3 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-xl shadow-lg shadow-purple-500/25">
                                        <Building2 className="w-5 h-5 text-white" />
                                    </div>
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent">
                                        Admin Portal
                                    </h1>
                                </div>
                            </button>
                        </div>

                        {/* Navigation */}
                        <nav className="hidden lg:flex items-center space-x-2">
                            {/* Main Nav Items */}
                            {mainNavItems.map((item) => {
                                const Icon = item.icon;
                                const active = isActive(item.href);
                                const isDisabled = item.requiresAccess && !hasAccess;

                                return (
                                    <button
                                        key={item.name}
                                        onClick={() => !isDisabled && navigate(item.href)}
                                        disabled={isDisabled}
                                        className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${isDisabled
                                            ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-50'
                                            : active
                                                ? 'bg-gradient-to-r from-indigo-500/10 to-purple-500/10 text-indigo-700 dark:text-indigo-300 shadow-sm'
                                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-slate-200'
                                            }`}
                                        title={isDisabled ? 'Requires active subscription or trial' : ''}
                                    >
                                        {active && (
                                            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 to-purple-500/5 rounded-xl"></div>
                                        )}
                                        <Icon className={`w-4 h-4 relative z-10 ${active ? 'text-indigo-600 dark:text-indigo-400' : ''}`} />
                                        <span className="relative z-10">{item.name}</span>
                                    </button>
                                );
                            })}

                            {/* Menu Groups */}
                            {menuGroups.map((menu) => {
                                const MenuIcon = menu.icon;
                                const isMenuActive = menu.items.some(item => isActive(item.href));
                                const hasActiveSubItem = menu.items.some(item => isActive(item.href));
                                const isMenuDisabled = menu.items.some(item => item.requiresAccess && !hasAccess);

                                return (
                                    <div
                                        key={menu.name}
                                        className="relative"
                                        onMouseEnter={() => setOpenMenu(menu.name)}
                                        onMouseLeave={() => setOpenMenu(null)}
                                    >
                                        <button
                                            className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors duration-200 ${isMenuDisabled
                                                ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-50'
                                                : isMenuActive || openMenu === menu.name
                                                    ? 'bg-gradient-to-r from-indigo-500/10 to-purple-500/10 text-indigo-700 dark:text-indigo-300'
                                                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-slate-200'
                                                }`}
                                        >
                                            <MenuIcon className={`w-4 h-4 ${isMenuActive || openMenu === menu.name ? 'text-indigo-600 dark:text-indigo-400' : ''}`} />
                                            <span>{menu.name}</span>
                                            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openMenu === menu.name ? 'rotate-180' : ''}`} />
                                        </button>

                                        {/* Dropdown Menu */}
                                        {openMenu === menu.name && (
                                            <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-800 py-1 z-50">
                                                {menu.items.map((item) => {
                                                    const ItemIcon = item.icon;
                                                    const active = isActive(item.href);
                                                    const isDisabled = item.requiresAccess && !hasAccess;

                                                    return (
                                                        <button
                                                            key={item.name}
                                                            onClick={() => {
                                                                if (!isDisabled) {
                                                                    navigate(item.href);
                                                                    setOpenMenu(null);
                                                                }
                                                            }}
                                                            disabled={isDisabled}
                                                            className={`w-full flex items-center space-x-3 px-4 py-2 text-sm font-medium transition-colors ${isDisabled
                                                                ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-50'
                                                                : active
                                                                    ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                                                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                                }`}
                                                        >
                                                            <ItemIcon className={`w-4 h-4 ${active ? 'text-indigo-600 dark:text-indigo-400' : ''}`} />
                                                            <span>{item.name}</span>
                                                            {active && (
                                                                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-600 dark:bg-indigo-400"></div>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </nav>

                        {/* User Menu */}
                        {user && (
                            <div
                                className="relative"
                                onMouseEnter={() => setOpenMenu('user')}
                                onMouseLeave={() => setOpenMenu(null)}
                            >
                                <button className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                    <div className="text-sm font-semibold text-slate-900 dark:text-white">
                                        {user.displayName || user.email?.split('@')[0]}
                                    </div>
                                    <ChevronDown className={`w-4 h-4 text-slate-600 dark:text-slate-400 transition-transform ${openMenu === 'user' ? 'rotate-180' : ''}`} />
                                </button>

                                {openMenu === 'user' && (
                                    <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50">
                                        {/* User Header */}
                                        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                                            <div className="flex items-center gap-3">

                                                <div>
                                                    <div className="text-sm font-semibold text-slate-900 dark:text-white">
                                                        {user.displayName || user.email?.split('@')[0]}
                                                    </div>
                                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                                        {user.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Menu Options */}
                                        <div className="py-1">
                                            {/* Hide Settings menu for individual subscriptions */}
                                            {!isIndividualSubscription && (
                                                <button
                                                    onClick={() => {
                                                        navigate('/subscription/settings');
                                                        setOpenMenu(null);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${isActive('/subscription/settings')
                                                        ? 'bg-indigo-50 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400'
                                                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                                                        }`}
                                                >
                                                    <Settings className="w-4 h-4" />
                                                    Settings
                                                </button>
                                            )}
                                            <button
                                                onClick={handleLogout}
                                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                Logout
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

            </header>

            {/* Mobile Menu Drawer - Outside header for proper z-index */}
            {mobileMenuOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] transition-opacity duration-300"
                        onClick={() => setMobileMenuOpen(false)}
                    />
                    {/* Sidebar */}
                    <div className="lg:hidden fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white dark:bg-slate-900 shadow-2xl z-[9999] transform transition-transform duration-300 ease-out overflow-y-auto">
                        <div className="p-6 space-y-4 h-full flex flex-col">
                            {/* Close Button */}
                            <div className="flex items-center justify-between mb-2 pb-4 border-b border-slate-200 dark:border-slate-700">
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Menu</h2>
                                <button
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                    aria-label="Close menu"
                                >
                                    <X className="w-6 h-6 text-slate-700 dark:text-slate-300" />
                                </button>
                            </div>

                            {/* Scrollable Content */}
                            <div className="flex-1 overflow-y-auto space-y-2">
                                {/* Main Nav Items */}
                                <div className="space-y-1">
                                    {mainNavItems.map((item) => {
                                        const Icon = item.icon;
                                        const active = isActive(item.href);
                                        const isDisabled = item.requiresAccess && !hasAccess;

                                        return (
                                            <button
                                                key={item.name}
                                                onClick={() => {
                                                    if (!isDisabled) {
                                                        navigate(item.href);
                                                        setMobileMenuOpen(false);
                                                    }
                                                }}
                                                disabled={isDisabled}
                                                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${isDisabled
                                                    ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-50'
                                                    : active
                                                        ? 'bg-gradient-to-r from-indigo-500/10 to-purple-500/10 text-indigo-700 dark:text-indigo-300 shadow-sm'
                                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/80'
                                                    }`}
                                            >
                                                <Icon className="w-5 h-5" />
                                                <span>{item.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Menu Groups */}
                                <div className="space-y-1 mt-4">
                                    {menuGroups.map((menu) => {
                                        const MenuIcon = menu.icon;
                                        const isMenuOpen = openMenu === menu.name;
                                        const isMenuActive = menu.items.some(item => isActive(item.href));

                                        return (
                                            <div key={menu.name} className="space-y-1">
                                                <button
                                                    onClick={() => setOpenMenu(isMenuOpen ? null : menu.name)}
                                                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${isMenuActive
                                                        ? 'bg-gradient-to-r from-indigo-500/10 to-purple-500/10 text-indigo-700 dark:text-indigo-300'
                                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/80'
                                                        }`}
                                                >
                                                    <div className="flex items-center space-x-3">
                                                        <MenuIcon className="w-5 h-5" />
                                                        <span>{menu.name}</span>
                                                    </div>
                                                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`} />
                                                </button>

                                                {isMenuOpen && (
                                                    <div className="pl-4 space-y-1">
                                                        {menu.items.map((item) => {
                                                            const ItemIcon = item.icon;
                                                            const active = isActive(item.href);
                                                            const isDisabled = item.requiresAccess && !hasAccess;

                                                            return (
                                                                <button
                                                                    key={item.name}
                                                                    onClick={() => {
                                                                        if (!isDisabled) {
                                                                            navigate(item.href);
                                                                            setMobileMenuOpen(false);
                                                                            setOpenMenu(null);
                                                                        }
                                                                    }}
                                                                    disabled={isDisabled}
                                                                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isDisabled
                                                                        ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-50'
                                                                        : active
                                                                            ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                                                                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                                        }`}
                                                                >
                                                                    <ItemIcon className="w-4 h-4" />
                                                                    <span>{item.name}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>

                            {/* User Menu - Mobile - Fixed at bottom */}
                            {user && (
                                <div className="space-y-1 border-t border-slate-200/50 dark:border-slate-800/50 pt-4 mt-auto">
                                    <div className="px-4 py-2 mb-2">
                                        <div className="text-sm font-semibold text-slate-900 dark:text-white">
                                            {user.displayName || user.email?.split('@')[0]}
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400">
                                            {user.email}
                                        </div>
                                    </div>
                                    {/* Hide Settings menu for individual subscriptions */}
                                    {!isIndividualSubscription && (
                                        <button
                                            onClick={() => {
                                                navigate('/subscription/settings');
                                                setMobileMenuOpen(false);
                                            }}
                                            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${isActive('/subscription/settings')
                                                ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                }`}
                                        >
                                            <Settings className="w-5 h-5" />
                                            <span>Settings</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            handleLogout();
                                            setMobileMenuOpen(false);
                                        }}
                                        className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all duration-200"
                                    >
                                        <LogOut className="w-5 h-5" />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {subscriptionLoading ? (
                    <div className="flex items-center justify-center min-h-[60vh]">
                        <div className="relative">
                            <div className="animate-spin rounded-full h-14 w-14 border-4 border-slate-200 dark:border-slate-800"></div>
                            <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-indigo-600 dark:border-indigo-400 absolute top-0 left-0"></div>
                        </div>
                    </div>
                ) : !hasAccess && !shouldSkipAccessCheck ? (
                    <div className="max-w-2xl mx-auto">
                        <div className="relative overflow-hidden bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-3xl shadow-2xl shadow-slate-900/10 dark:shadow-black/50 p-10 text-center border border-slate-200/50 dark:border-slate-800/50">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none"></div>
                            <div className="relative z-10">
                                <div className="mb-8">
                                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25 mb-6">
                                        <CreditCard className="w-10 h-10 text-white" />
                                    </div>
                                    <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent mb-3">
                                        Subscription Required
                                    </h2>
                                    <p className="text-lg text-slate-600 dark:text-slate-400 mb-2">
                                        This feature requires an active subscription or trial period.
                                    </p>
                                    <p className="text-sm text-slate-500 dark:text-slate-500 font-medium">
                                        Start a free 7-day trial to access all features.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('[SubscriptionLayout] Start Free Trial clicked', {
                                            user: !!user,
                                            organizationId,
                                            hasAccess,
                                            subscriptionLoading
                                        });
                                        // Only navigate with org parameter if user has a valid organization
                                        // Otherwise, let onboarding page handle subscription type selection
                                        try {
                                            if (organizationId) {
                                                console.log('[SubscriptionLayout] Navigating to onboarding with org:', organizationId);
                                                navigate(`/onboarding?org=${organizationId}`);
                                            } else {
                                                console.log('[SubscriptionLayout] Navigating to onboarding without org');
                                                navigate('/onboarding');
                                            }
                                        } catch (error) {
                                            console.error('[SubscriptionLayout] Error navigating:', error);
                                        }
                                    }}
                                    disabled={!user}
                                    className="group relative z-20 px-8 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-purple-500/25 font-semibold text-base transition-all duration-300 hover:scale-[1.02] flex items-center space-x-3 mx-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                    <span>Start Free Trial</span>
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    children
                )}
            </main>
        </div>
    );
};

export default SubscriptionLayout;

